import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, User, Settings, Bell, Languages, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../auth/AuthProvider";
import { api, ROLE_LABELS } from "../lib/api";
import { SUPPORTED_LOCALES } from "../i18n";

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function UserMenu() {
  const { user, role, signOut, refreshMe } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  if (!user) return null;

  const onSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const onLocaleChange = async (locale) => {
    if (locale === i18n.language) return;
    // Switch UI immediately — persistence is best-effort.
    try {
      await i18n.changeLanguage(locale);
    } catch {
      /* ignore */
    }
    try {
      await api.setLocale(locale);
      // Refresh /me so AuthProvider sees the new user.locale.
      if (refreshMe) await refreshMe();
    } catch {
      // Network/auth failure leaves the UI in the chosen locale but
      // unpersisted — next reload reverts to the server value.
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="user-menu"
        className="h-9 w-9 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={t("common.profile")}
      >
        {initials(user.full_name)}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="font-medium text-zinc-900 truncate">
            {user.full_name}
          </div>
          <div className="text-xs text-zinc-500 truncate">{user.email}</div>
          {role && (
            <div className="text-[11px] uppercase tracking-wider text-primary mt-1">
              {ROLE_LABELS[role] || role}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings/organization")}>
          <Settings className="h-4 w-4 mr-2" />
          {t("settings.organization")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/team")}>
          <User className="h-4 w-4 mr-2" />
          {t("nav.team")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings/notifications")}>
          <Bell className="h-4 w-4 mr-2" />
          {t("nav.notifications")}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages className="h-4 w-4 mr-2" />
            {t("common.language")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {SUPPORTED_LOCALES.map((code) => (
              <DropdownMenuItem
                key={code}
                onClick={() => onLocaleChange(code)}
                data-testid={`locale-${code}`}
              >
                <Check
                  className={`h-4 w-4 mr-2 ${
                    i18n.language === code ? "opacity-100" : "opacity-0"
                  }`}
                />
                {t(`languages.${code}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onSignOut}
          data-testid="signout"
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {t("common.sign_out")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
